import React from "react";
import { createPortal } from "react-dom";
import { Check, Info } from "lucide-react";
import { Button } from "../../../design-system/components/primitives/Button/Button";
import {
  Actions,
  Aside,
  Body,
  Card,
  CARD_WIDTH,
  CARD_WIDTH_WIDE,
  Facts,
  ListColumn,
  OptionMedia,
  Head,
  HeadText,
  List,
  ListLabel,
  Note,
  Option,
  OptionText,
  Subtitle,
  Tag,
  Title,
} from "./PickerCard.styles";
import type { PickerCardAction, PickerCardProps } from "./PickerCard.types";

/** A floating card with a preview, actions and an optional list to pick from. */
export const PickerCard: React.FC<PickerCardProps> = ({
  id,
  open,
  rect,
  cardRef,
  ariaLabel,
  title,
  subtitle,
  tag,
  tone = "default",
  size = "default",
  preview,
  facts,
  note,
  actions = [],
  actionsPlacement = "body",
  onAction,
  optionsLabel,
  options,
  onSelect,
  onOptionPreview,
  onPointerEnter,
  onPointerLeave,
  onKeyDown,
}) => {
  if (!(open && rect)) return null;
  const hasList = Boolean(options?.length);
  /** Wide is for the picker: without a list to switch there is nothing for a second column. */
  const wide = size === "wide" && hasList;
  const width = wide ? CARD_WIDTH_WIDE : CARD_WIDTH;
  const maxHeight = rect.maxHeight > 0 ? `min(${rect.maxHeight}px, calc(100vh - 1rem))` : undefined;

  const actionButtons = (list: PickerCardAction[]) =>
    list.map((action) => (
      <Button
        key={action.id}
        variant={action.look === "primary" ? "primary" : action.look === "outline" ? "secondary-outline" : "secondary"}
        size="sm"
        iconBefore={action.icon}
        onClick={() => onAction?.(action.id)}
      >
        {action.label}
      </Button>
    ));
  const actionRow = actions.length ? <Actions data-part="actions">{actionButtons(actions)}</Actions> : null;
  const noteBlock = note ? (
    <Note>
      <Info aria-hidden="true" />
      <span>{note}</span>
    </Note>
  ) : null;
  const factsBlock = facts?.length ? (
    <Facts data-part="facts">
      {facts.map((fact) => (
        <li key={fact}>{fact}</li>
      ))}
    </Facts>
  ) : null;

  const list = hasList ? (
    <>
      {optionsLabel ? <ListLabel id={`${id}-options`}>{optionsLabel}</ListLabel> : <span />}
      <List
        role="listbox"
        data-part="list"
        $scroll={wide}
        aria-labelledby={optionsLabel ? `${id}-options` : undefined}
        onMouseLeave={() => onOptionPreview?.(null)}
      >
        {options?.map((option) => (
          <Option
            key={option.value || "default"}
            type="button"
            role="option"
            $media={Boolean(option.media)}
            aria-selected={option.selected}
            onClick={() => onSelect?.(option.value)}
            onMouseEnter={() => onOptionPreview?.(option.value)}
            onFocus={() => onOptionPreview?.(option.value)}
          >
            {option.media ? <OptionMedia>{option.media}</OptionMedia> : null}
            <OptionText>
              <b>{option.title}</b>
              <span>{option.description}</span>
              {option.facts?.length ? <span>{option.facts.join(" · ")}</span> : null}
            </OptionText>
            {option.selected ? <Check aria-hidden="true" /> : <span />}
          </Option>
        ))}
      </List>
    </>
  ) : null;

  return createPortal(
    <Card
      ref={cardRef}
      id={id}
      role="dialog"
      aria-label={ariaLabel}
      $wide={wide}
      data-size={wide ? "wide" : "default"}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
      onKeyDown={onKeyDown}
      style={{
        ...(rect.bottom != null ? { bottom: rect.bottom, top: "auto" } : { top: rect.top, bottom: "auto" }),
        left: `min(${rect.left}px, calc(100vw - ${width} - 0.5rem))`,
        ...(maxHeight ? { maxHeight } : {}),
      }}
    >
      <Head>
        <HeadText>
          <Title $error={tone === "error"}>{title}</Title>
          {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
        </HeadText>
        {tag ? <Tag>{tag}</Tag> : null}
        {actionsPlacement === "header" && actions.length ? <Actions data-part="header-actions">{actionButtons(actions)}</Actions> : null}
      </Head>
      {wide ? (
        <Body data-part="body">
          {hasList ? <ListColumn>{list}</ListColumn> : null}
          <Aside data-part="aside">
            {preview}
            {factsBlock}
            {noteBlock}
            {actionsPlacement === "header" ? null : actionRow}
          </Aside>
        </Body>
      ) : (
        <>
          {preview}
          {factsBlock}
          {noteBlock}
          {actionsPlacement === "header" ? null : actionRow}
          {list}
        </>
      )}
    </Card>,
    document.body,
  );
};

export default PickerCard;
